/**
 * Measured hover budget: the `auto` hover preview keeps real voxels until they are measured to be
 * slow on this machine, in this scene, for this material (`src/voxels/hoverPreviewMode.ts`).
 *
 * Why measured and not a radius rule: the cost of a real hover preview is set by the scene far more
 * than by the brush (lab bench `/solid-bench`, `__hueboBench.runHover`). On an M-series laptop a solid
 * r32 hover over a 256³ terrain costs about 8 ms of main thread and no extra GPU, while any hover that
 * touches a large SDF blob re-marches the whole blob component on the GPU: 15 ms (64² blob top) to
 * 50 ms (128² blob top) per update even at r2. A tablet sits somewhere else on both curves.
 *
 * One hover update is measured from three hooks, each a few lines in existing code:
 * - `noteHoverPreviewIssued`: an idle hover preview event was built (previewEventsAtom);
 * - `noteHoverCompose`: the composition of those preview events took this long (composedStateAtom);
 * - `noteHoverFrameFlushed`: the next frame flushed the renderers (VoxelRendererFrame's frame bridge),
 *   with that flush's CPU time and a probe of the GPU work it queued (`queue.onSubmittedWorkDone`).
 * The sample's cost is the larger of CPU (compose + flush) and GPU (flush submit to work done).
 *
 * Rule (per previewed type: the palette type, or `erase`):
 * - real voxels at r <= HOVER_ALWAYS_REAL_RADIUS and while nothing was measured;
 * - when the median of the last 3 real hover updates exceeds HOVER_FRAME_BUDGET_MS, the type gets a
 *   verdict `slowRadius` = the current radius: a ghost from there up, real voxels below it. The median
 *   ignores a one-off spike (a pipeline compile on the first blob hover, a GC);
 * - hysteresis: a verdict is not re-measured while it holds, so the preview never flips back and forth.
 *   It is dropped when it is older than VERDICT_TTL_MS *and* the hover has paused for HOVER_GAP_MS
 *   (pointer left the canvas, a stroke, a tool change): the next hover measures again from real voxels;
 * - a static ceiling as a safety net: above HOVER_GHOST_CEILING_RADIUS always a ghost.
 * Until a frame bridge has installed the budget (unit tests, no renderer) `auto` keeps the old static
 * rule (`HOVER_GHOST_MIN_RADIUS`).
 *
 * Module state, not store state: it describes this machine and is read only at decision time.
 * `__huebo.hoverCost()` (dev builds) returns the verdicts and the recent samples.
 */

/** A hover update costing more than this (ms of CPU or GPU) misses a 60 Hz frame. */
export const HOVER_FRAME_BUDGET_MS = 16
/** Brushes up to this radius always preview as real voxels (pencil sizes; nothing to save). */
export const HOVER_ALWAYS_REAL_RADIUS = 1
/** Above this radius `auto` is always a ghost, measured or not (beyond the UI's brush sizes). */
export const HOVER_GHOST_CEILING_RADIUS = 32
/** Real hover updates whose median decides (a one-off spike never trips the switch). */
export const HOVER_SAMPLE_WINDOW = 3
/** A verdict older than this may be re-measured at the next hover pause. */
export const VERDICT_TTL_MS = 30_000
/** No hover decision for this long counts as a pause (the preview was not on screen). */
export const HOVER_GAP_MS = 400
/** A pending update whose frame never came (nothing changed) is dropped after this long. */
const PENDING_TIMEOUT_MS = 250

export type HoverCostSample = { key: string; radius: number; cpuMs: number; gpuMs: number | null; costMs: number }
type Verdict = { slowRadius: number; at: number; medianMs: number }
type Pending = { key: string; radius: number; at: number; composeMs: number }
type GpuQueue = { onSubmittedWorkDone: () => Promise<unknown> }

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now())

export class HoverPreviewBudget {
  private installed = 0
  private readonly verdicts = new Map<string, Verdict>()
  private readonly windows = new Map<string, { radius: number; costs: number[] }>()
  private readonly recent: HoverCostSample[] = []
  private pending: Pending | null = null
  private lastDecisionAt = -Infinity

  constructor(private readonly clock: () => number = now) {}

  /** The frame bridge is live: decisions follow measurements. Returns the uninstall function. */
  install(): () => void {
    this.installed++
    return () => {
      this.installed = Math.max(0, this.installed - 1)
    }
  }

  get active(): boolean {
    return this.installed > 0
  }

  /**
   * Whether `auto` previews `key` at `radius` as real voxels. Called for every hover evaluation (the
   * preview events and the ghost atom both ask, in the same store update, and get the same answer).
   */
  allowsVoxels(key: string, radius: number): boolean {
    if (radius <= HOVER_ALWAYS_REAL_RADIUS) return true
    if (radius > HOVER_GHOST_CEILING_RADIUS) return false
    const t = this.clock()
    const gap = t - this.lastDecisionAt
    this.lastDecisionAt = t
    const verdict = this.verdicts.get(key)
    if (!verdict) return true
    if (gap >= HOVER_GAP_MS && t - verdict.at >= VERDICT_TTL_MS) {
      this.verdicts.delete(key)
      this.windows.delete(key)
      return true
    }
    return radius < verdict.slowRadius
  }

  /** An idle hover built a real-voxel preview event for `key` at `radius`. */
  noteHoverPreviewIssued(key: string, radius: number): void {
    if (!this.active) return
    this.pending = { key, radius, at: this.clock(), composeMs: 0 }
  }

  /** Composition time of the current preview events (counted only for a pending hover update). */
  noteHoverCompose(ms: number): void {
    if (this.pending) this.pending.composeMs += ms
  }

  /**
   * The frame after a hover update flushed its renderers in `flushMs`. `queue` (WebGPU) probes the GPU
   * work the flush queued; without it the sample is CPU only.
   */
  noteHoverFrameFlushed(flushMs: number, queue: GpuQueue | null): void {
    const p = this.pending
    if (!p) return
    this.pending = null
    if (this.clock() - p.at > PENDING_TIMEOUT_MS) return
    const cpuMs = p.composeMs + flushMs
    if (!queue) {
      this.record({ key: p.key, radius: p.radius, cpuMs, gpuMs: null, costMs: cpuMs })
      return
    }
    const submitted = this.clock()
    let done: Promise<unknown>
    try {
      done = queue.onSubmittedWorkDone()
    } catch {
      this.record({ key: p.key, radius: p.radius, cpuMs, gpuMs: null, costMs: cpuMs })
      return
    }
    void done.then(
      () => {
        const gpuMs = this.clock() - submitted
        this.record({ key: p.key, radius: p.radius, cpuMs, gpuMs, costMs: Math.max(cpuMs, gpuMs) })
      },
      () => undefined,
    )
  }

  /**
   * Records one measured real-voxel hover update. The window restarts when the radius changes; when its
   * median is over budget the type's `slowRadius` drops to this radius.
   */
  record(sample: HoverCostSample): void {
    this.recent.push(sample)
    if (this.recent.length > 32) this.recent.shift()
    const verdict = this.verdicts.get(sample.key)
    if (verdict && sample.radius >= verdict.slowRadius) return
    let window = this.windows.get(sample.key)
    if (!window || window.radius !== sample.radius) {
      window = { radius: sample.radius, costs: [] }
      this.windows.set(sample.key, window)
    }
    window.costs.push(sample.costMs)
    if (window.costs.length > HOVER_SAMPLE_WINDOW) window.costs.shift()
    if (window.costs.length < HOVER_SAMPLE_WINDOW) return
    const medianMs = median(window.costs)
    if (medianMs <= HOVER_FRAME_BUDGET_MS) return
    this.verdicts.set(sample.key, { slowRadius: sample.radius, at: this.clock(), medianMs })
    this.windows.delete(sample.key)
  }

  /** Forgets every measurement (a new document; tests). */
  reset(): void {
    this.verdicts.clear()
    this.windows.clear()
    this.recent.length = 0
    this.pending = null
    this.lastDecisionAt = -Infinity
  }

  debugState(): { active: boolean; verdicts: Record<string, Verdict>; recent: HoverCostSample[] } {
    return { active: this.active, verdicts: Object.fromEntries(this.verdicts), recent: this.recent.slice() }
  }
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length >> 1
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** The app's budget (one per page: it describes the machine). */
export const hoverPreviewBudget = new HoverPreviewBudget()

if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  const w = window as unknown as { __huebo?: Record<string, unknown> }
  const hooks = (w.__huebo ??= {})
  hooks.hoverCost = () => hoverPreviewBudget.debugState()
  hooks.hoverCostReset = () => hoverPreviewBudget.reset()
}
